import React, { useEffect } from 'react';
import orgConfig from 'org/configs/org.config';
import { useSelector, useDispatch } from 'react-redux';
import { postInstoreCardLink } from 'core/actions/member';
import { clearInstoreLinkFailure } from 'core/modules/Instore2/actions';
import { selectEasyLinkSuppression } from 'core/selectors/user';
import { selectDynamicCardLinksFeedback } from 'core/modules/Instore2/selectors';
import { useApiFeedbackManager } from 'core/hooks/useApiFeedbackManager';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import { INSTORE_OFFER_MODAL, ME_PAGE_INSTORE_ONLY } from 'core/modules/Instore2/utils/constants';
import {
  LinkWarning, Cards, Logo, Disclaimer, ManageCardsButton, UnlinkConfirmation,
  useUnlinkOfferButton,
} from 'core/modules/Instore2/partials';
import { InstoreOfferModalTitle, InstoreOnlyPill } from '../partials';

import '../InstoreOfferDetails.scss';

const { instoreLabel } = orgConfig;

function WithLinkedCardsView({ offer, isModal, isLoaderInverted }) {
  const dispatch = useDispatch();
  const {
    displayRate, currency, rclonMerchantName, daysToExpire,
    disclaimer, offerId, hasEligibleCards, eligibleCards,
  } = offer;
  const initiator = isModal ? INSTORE_OFFER_MODAL : ME_PAGE_INSTORE_ONLY;

  const easyLinkSuppression = useSelector(selectEasyLinkSuppression);
  const dynamicCardLinksFeedback = useSelector(selectDynamicCardLinksFeedback);

  const {
    UnlinkOfferButton, shouldShowConfirm, showConfirmMessage, hideConfirmMessage,
  } = useUnlinkOfferButton(initiator, rclonMerchantName);

  const { clearFeedback, feedbackData } = useApiFeedbackManager({
    data: dynamicCardLinksFeedback,
    callbackAction: clearInstoreLinkFailure,
  });

  const { hasLinkFailedFeedback } = feedbackData;

  useEffect(() => {
    if (!easyLinkSuppression && hasEligibleCards) {
      // autolink unlinked cards
      eligibleCards.forEach(({ cardId }) => {
        dispatch(postInstoreCardLink(offerId, { cardId }, {}, { autoLink: true, initiator, offer }));
      });
    }
  }, [easyLinkSuppression, hasEligibleCards]);

  return (
    <div className="mn_instoreOfferDetails mn_withLinkedCardsView">
      {!isModal && <InstoreOnlyPill />}
      {!isModal && <Logo offer={offer} />}
      <InstoreOfferModalTitle offer={offer} />
      <div className="mn_offerDataWrap">
        <div id="mn_dialogDescription" className="mn_offerInfo">
          {daysToExpire} day{daysToExpire === 1 ? '' : 's'} left
          to earn {displayRate} {currency} {instoreLabel} at {rclonMerchantName}
          <span className="mn_dot">.</span>
        </div>
        <LinkWarning hasError={hasLinkFailedFeedback} feedbackData={feedbackData} />
        <Cards offer={offer} clearWarning={clearFeedback} initiator={initiator} isLoaderInverted={isLoaderInverted} />
        <div className="mn_buttons">
          <ManageCardsButton />
          <span className="mn_divider">|</span>
          <UnlinkOfferButton onClickCallback={showConfirmMessage} className="mn_basicButton mn_unlinkOfferButton" />
        </div>
      </div>
      <UnlinkConfirmation
        shouldShowConfirm={shouldShowConfirm}
        offer={offer}
        onHideClick={hideConfirmMessage}
        onUnlinkClick={clearFeedback}
        initiator={initiator}
        isLoaderInverted={isLoaderInverted}
      />
      <Disclaimer>{disclaimer}</Disclaimer>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><WithLinkedCardsView {...props} /></ComponentErrorBoundary>;
